import React from 'react'
import { SubscriptionPlan } from '@/types/subscription'
import { CheckIcon } from '@heroicons/react/24/outline'
import { cn } from '@/utils/cn'
import { CurrencyDisplay } from '@/components/currency'

interface SubscriptionPlanCardProps {
    plan: SubscriptionPlan
    isCurrentTier?: boolean
    onSelectPlan: (tier: string) => void
    loading?: boolean
    userRole?: 'worker' | 'employer'
}

export const SubscriptionPlanCard: React.FC<SubscriptionPlanCardProps> = ({
    plan,
    isCurrentTier = false,
    onSelectPlan,
    loading = false,
    userRole = 'worker',
}) => {
    const getLimitValue = (limit?: number) =>
        typeof limit === 'number' ? limit.toLocaleString() : '—'

    const gigLimit = plan.limits?.gigs ?? plan.monthlyLimits?.gigs
    const applicationLimit = plan.limits?.applications ?? plan.monthlyLimits?.applications

    // Build features array based on user role
    const baseFeatures = []

    // Show role-specific limits
    if (userRole === 'employer') {
        baseFeatures.push({
            key: 'gigs',
            label: `${getLimitValue(gigLimit)} Gigs/month`,
            enabled: true,
        })
    } else {
        baseFeatures.push({
            key: 'applications',
            label: `${getLimitValue(applicationLimit)} Applications/month`,
            enabled: true,
        })
    }

    const features = [
        ...baseFeatures,
        {
            key: 'commission',
            label: `${(plan.platformCommission * 100).toFixed(0)}% Platform Commission`,
            enabled: true,
        },
        {
            key: 'protection',
            label: `${(plan.buyerProtectionFee * 100).toFixed(0)}% Buyer Protection Fee`,
            enabled: true,
        },
        {
            key: 'analytics',
            label: 'Advanced Analytics',
            enabled: plan.features?.advancedAnalytics,
        },
        {
            key: 'support',
            label: 'Priority Support',
            enabled: plan.features?.prioritySupport,
        },
        {
            key: 'api',
            label: 'API Access',
            enabled: plan.features?.apiAccess,
        },
        {
            key: 'withdrawals',
            label: `${plan.features?.freeWithdrawals || 0} Free Withdrawals/month`,
            enabled: (plan.features?.freeWithdrawals || 0) > 0,
        },
        {
            key: 'verification',
            label: 'Verification Badge',
            enabled: plan.features?.verificationBadge ?? false,
        },
    ]

    return (
        <div
            className={cn(
                'relative rounded-2xl border-2 p-4 transition-all duration-300 sm:p-5',
                isCurrentTier
                    ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20'
                    : 'border-slate-200 bg-white hover:border-primary-400 dark:border-gray-800 dark:bg-gray-900'
            )}
        >
            {isCurrentTier && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-block bg-blue-500 px-3 py-1 text-xs font-semibold text-white rounded-full">
                        Current Plan
                    </span>
                </div>
            )}

            <div className="mb-4">
                <h3 className="mb-1 text-base font-bold text-slate-900 dark:text-white sm:text-lg">
                    {plan.name}
                </h3>

                <div className="flex items-baseline gap-1">
                    <CurrencyDisplay
                        amount={plan.price}
                        currency="GHS"
                        size="xl"
                        variant="default"
                        className="text-2xl font-bold sm:text-3xl"
                    />
                    <span className="text-gray-600 dark:text-gray-400">/month</span>
                </div>

                {plan.description && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                        {plan.description}
                    </p>
                )}
            </div>

            <button
                onClick={() => onSelectPlan(plan.tier)}
                disabled={isCurrentTier || loading}
                className={cn(
                    'mb-4 w-full rounded-xl px-4 py-2 text-sm font-semibold transition-colors duration-200 sm:py-2.5',
                    isCurrentTier
                        ? 'cursor-not-allowed bg-gray-300 text-gray-600 dark:bg-gray-600 dark:text-gray-400'
                        : 'bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-300'
                )}
            >
                {isCurrentTier ? 'Current Plan' : loading ? 'Processing...' : 'Select Plan'}
            </button>

            <div className="space-y-2.5">
                {features.map((feature) => (
                    <div
                        key={feature.key}
                        className={cn(
                            'flex items-start gap-3',
                            !feature.enabled && 'opacity-50'
                        )}
                    >
                        {feature.enabled ? (
                            <CheckIcon className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5 sm:w-5 sm:h-5" />
                        ) : (
                            <div className="w-5 h-5 bg-gray-300 dark:bg-gray-600 rounded-full flex-shrink-0 mt-0.5" />
                        )}
                        <span
                            className={cn(
                                'text-xs sm:text-sm',
                                feature.enabled
                                    ? 'text-gray-700 dark:text-gray-300'
                                    : 'text-gray-500 dark:text-gray-500'
                            )}
                        >
                            {feature.label}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    )
}